import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

console.log('[header-drawer-desktop] module loaded');

/**
 * Desktop header drawer with a two-level slide-out menu.
 *
 * Submenus are siblings of the main drawer panel in the DOM, so they can paint
 * *behind* the main drawer via a lower z-index. JS tracks which submenu is
 * active and toggles classes/aria state.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details
 * @property {HTMLDivElement} menuDrawer
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawerDesktop extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {string | null} */
  _activeSubmenuId = null;

  /** @type {boolean} */
  _closing = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    this.#scheduleApplyBlockData();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this._blockDataObserver?.disconnect();
    this._blockDataObserver = null;
  }

  /** @param {KeyboardEvent} event */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    if (this._activeSubmenuId) {
      this.closeSubmenu();
    } else {
      this.close();
    }
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Toggle the main drawer. Prevents the native <summary> toggle so we can
   * control the [open] attribute (and play the close animation) ourselves.
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open(event);
  }

  /**
   * Open the main drawer.
   * @param {Event} [event]
   */
  open(event) {
    event?.preventDefault();

    const details = this.refs.details;
    const summary = details.querySelector('summary');
    if (!summary) return;

    this._closing = false;

    // Measure the currently visible bottom of the header group so the drawer
    // content lines up with whatever portion of the header is on screen right
    // now (announcement bar may be scrolled away).
    this.#updateHeaderOffset();

    if (!details.hasAttribute('open')) details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      details.classList.add('menu-desktop-open');
      onAnimationEnd(this.refs.menuDrawer, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Close the main drawer. If a submenu is open, it closes first (its slide
   * animation plays out) and only then the main drawer slides away.
   */
  close() {
    if (this._activeSubmenuId) {
      const activeSubmenu = this.querySelector(`#${CSS.escape(this._activeSubmenuId)}`);
      this.#clearActiveSubmenu({ instant: false });

      if (activeSubmenu instanceof HTMLElement) {
        onAnimationEnd(activeSubmenu, () => this.#closeMainDrawer(), { subtree: false });
        return;
      }
    }
    this.#closeMainDrawer();
  }

  /**
   * Actually close the main drawer panel.
   */
  #closeMainDrawer() {
    const details = this.refs.details;
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-desktop-open');
    this._closing = true;

    onAnimationEnd(
      this.refs.menuDrawer,
      () => {
        if (!this._closing || details.classList.contains('menu-desktop-open')) return;
        this._closing = false;
        details.removeAttribute('open');
        this.#resetSubmenus();
        removeTrapFocus();
      },
      { subtree: false }
    );
  }

  /**
   * Open a submenu by its id. If another submenu is already open the swap
   * happens instantly (no slide animation) to avoid visual jumps — both
   * submenus stay in layout, only the `--open` class swaps.
   * @param {string} submenuId
   * @param {Event} [event]
   */
  openSubmenu(submenuId, event) {
    event?.preventDefault();

    const submenu = this.querySelector(`#${CSS.escape(submenuId)}`);
    const trigger = this.querySelector(`[data-submenu-target="${submenuId}"]`);
    if (!(submenu instanceof HTMLElement) || !(trigger instanceof HTMLElement)) return;

    // Re-measure offsets — the header may have shifted between open() and now
    // (sticky behaviour, announcement bar scroll), and the submenu padding-top
    // is bound to the page logo's current viewport position.
    this.#updateHeaderOffset();

    // Toggle: clicking the active submenu's trigger closes it.
    if (this._activeSubmenuId === submenuId) {
      this.closeSubmenu();
      return;
    }

    const isSwitching = this._activeSubmenuId !== null;

    if (isSwitching) {
      // Disable transitions on all submenus so the swap is instant.
      // Class goes on the host element because submenus are siblings of menuDrawer.
      this.classList.add('header-drawer-desktop--switching-submenu');
      // Remove `--open` from the previous one but keep it rendered (hidden=false)
      // so there's no display:none flicker during the swap.
      this.#deactivatePreviousSubmenu();
    }

    // Reveal and activate the new submenu
    submenu.hidden = false;
    // Force a style flush so the browser commits the pre-transition state
    void submenu.offsetWidth;
    submenu.classList.add('menu-drawer-desktop__submenu--open');
    trigger.setAttribute('aria-expanded', 'true');
    this._activeSubmenuId = submenuId;

    if (isSwitching) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.classList.remove('header-drawer-desktop--switching-submenu');
        });
      });
    }
  }

  /**
   * Close the currently open submenu (with animation).
   */
  closeSubmenu() {
    this.#clearActiveSubmenu({ instant: false });
  }

  /**
   * Remove `--open` from the currently active submenu without touching its
   * `hidden` attribute. Used during a submenu switch.
   */
  #deactivatePreviousSubmenu() {
    if (!this._activeSubmenuId) return;

    const submenu = this.querySelector(`#${CSS.escape(this._activeSubmenuId)}`);
    const trigger = this.querySelector(`[data-submenu-target="${this._activeSubmenuId}"]`);

    if (submenu instanceof HTMLElement) {
      submenu.classList.remove('menu-drawer-desktop__submenu--open');
    }
    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    this._activeSubmenuId = null;
  }

  /**
   * Remove the open state from the active submenu.
   * @param {{instant: boolean}} options
   */
  #clearActiveSubmenu({ instant }) {
    if (!this._activeSubmenuId) return;

    const submenu = this.querySelector(`#${CSS.escape(this._activeSubmenuId)}`);
    const trigger = this.querySelector(`[data-submenu-target="${this._activeSubmenuId}"]`);

    if (submenu instanceof HTMLElement) {
      submenu.classList.remove('menu-drawer-desktop__submenu--open');
      if (instant) {
        submenu.hidden = true;
      } else {
        onAnimationEnd(
          submenu,
          () => {
            if (!submenu.classList.contains('menu-drawer-desktop__submenu--open')) {
              submenu.hidden = true;
            }
          },
          { subtree: false }
        );
      }
    }

    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    this._activeSubmenuId = null;
  }

  /**
   * Force every submenu back to its closed state.
   */
  #resetSubmenus() {
    const submenus = this.querySelectorAll('.menu-drawer-desktop__submenu');
    submenus.forEach((submenu) => {
      if (!(submenu instanceof HTMLElement)) return;
      submenu.classList.remove('menu-drawer-desktop__submenu--open');
      submenu.hidden = true;
    });
    const triggers = this.querySelectorAll('[data-submenu-target]');
    triggers.forEach((trigger) => {
      if (trigger instanceof HTMLElement) trigger.setAttribute('aria-expanded', 'false');
    });
    this._activeSubmenuId = null;
  }

  /**
   * Measure visible header geometry and expose it as CSS variables on the
   * host. Variables:
   *  - `--drawer-desktop-header-visible-bottom`: viewport y of the bottom
   *    edge of the visible header area (used as main drawer content offset).
   *  - `--drawer-desktop-logo-top`: viewport y of the top edge of the page
   *    logo's visible image (used to align the submenu's first row with it).
   */
  #updateHeaderOffset() {
    console.log('[header-drawer-desktop] #updateHeaderOffset');

    const headerGroup = document.querySelector('#header-group');
    const headerComponent = document.querySelector('header-component');

    const bottomCandidates = [headerGroup, headerComponent]
      .filter((el) => el instanceof HTMLElement)
      .map((el) => el.getBoundingClientRect().bottom);
    const bottom = Math.max(0, Math.round(Math.max(0, ...bottomCandidates)));
    this.style.setProperty('--drawer-desktop-header-visible-bottom', `${bottom}px`);

    const scopes = [headerComponent, headerGroup].filter((el) => el instanceof HTMLElement);
    const logoNodes = scopes
      .flatMap((scope) => Array.from(scope.querySelectorAll('.page-header-logo-img')))
      .filter((node) => node instanceof HTMLElement)
      .filter((node) => !this.contains(node))
      .filter((node) => {
        const r = node.getBoundingClientRect();
        return r.width > 0 || r.height > 0;
      });

    console.log('[header-drawer-desktop] logo candidates:', logoNodes);

    const logo = logoNodes[0];
    if (logo) {
      const logoTop = Math.max(0, Math.round(logo.getBoundingClientRect().top));
      console.log('[header-drawer-desktop] logoTop =', logoTop, 'from', logo);
      this.style.setProperty('--drawer-desktop-logo-top', `${logoTop}px`);
    } else {
      console.log('[header-drawer-desktop] no logo node found');
      this.style.removeProperty('--drawer-desktop-logo-top');
    }
  }

  /** @type {MutationObserver | null} */
  _blockDataObserver = null;

  /**
   * Wait until the children of this custom element are parsed, then move
   * block data into menu items. Re-runs whenever the section is morphed
   * (theme editor sync, section reload) and re-injects a fresh slot.
   */
  #scheduleApplyBlockData() {
    // Try immediately if the slot is already there.
    if (this.querySelector('[data-drawer-block-data]')) {
      this.#applyBlockData();
    } else if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#applyBlockData(), { once: true });
    } else {
      // One-shot rAF in case the parser hasn't reached our closing tag yet.
      requestAnimationFrame(() => this.#applyBlockData());
    }

    // Persistent observer — re-apply if the section is re-rendered and a
    // fresh, unprocessed slot appears in our subtree.
    this._blockDataObserver?.disconnect();
    this._blockDataObserver = new MutationObserver(() => {
      const slot = this.querySelector('[data-drawer-block-data]:not([data-applied])');
      if (slot) this.#applyBlockData();
    });
    this._blockDataObserver.observe(this, { childList: true, subtree: true });
  }

  /**
   * Move per-handle child theme block output (images, badges) into the
   * matching menu items. Child blocks render themselves into a hidden slot
   * (`[ref="blockData"]`); each carries `data-drawer-image-for` or
   * `data-drawer-badge-for` plus, for badges, `data-drawer-badge-level`
   * ("main" | "child"). We match against the menu item's `data-link-handle`.
   */
  #applyBlockData() {
    const slot = this.querySelector('[data-drawer-block-data]:not([data-applied])');
    if (!(slot instanceof HTMLElement)) return;

    // Images — if the handle matches a main-level link that has a submenu,
    // mount the image inside that submenu panel. Otherwise, fall back to
    // placing it on the main menu item itself (leaf link case).
    const images = slot.querySelectorAll('[data-drawer-image-for]');
    images.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const handle = node.getAttribute('data-drawer-image-for');
      if (!handle) return;

      const triggerId = `HeaderDrawerDesktop-${handle}`;
      const submenu = this.querySelector(
        `.menu-drawer-desktop__submenu[aria-labelledby="${CSS.escape(triggerId)}"]`
      );

      if (submenu instanceof HTMLElement) {
        // Place image inside the submenu — prefer a known inner wrapper if
        // present, otherwise prepend to the submenu panel itself.
        const inner =
          submenu.querySelector('.menu-drawer-desktop__submenu-inner') ||
          submenu.querySelector('.menu-drawer-desktop__submenu-content') ||
          submenu;
        inner.prepend(node);
        submenu.classList.add('menu-drawer-desktop__submenu--with-image');
        return;
      }

      // Fallback: leaf link — render inline on the main row.
      const target = this.querySelector(
        `.menu-drawer-desktop__menu-item--mainlist[data-link-handle="${CSS.escape(handle)}"]`
      );
      if (!(target instanceof HTMLElement)) return;
      target.classList.add('menu-drawer-desktop__menu-item--with-image');
      const textEl = target.querySelector('.menu-drawer-desktop__menu-item-text');
      if (textEl) {
        target.insertBefore(node, textEl);
      } else {
        target.prepend(node);
      }
    });

    // Badges — main or child level.
    const badges = slot.querySelectorAll('[data-drawer-badge-for]');
    badges.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const handle = node.getAttribute('data-drawer-badge-for');
      if (!handle) return;
      const level = node.getAttribute('data-drawer-badge-level') === 'child' ? 'child' : 'main';
      const selector =
        level === 'child'
          ? `.menu-drawer-desktop__menu-item--child[data-link-handle="${CSS.escape(handle)}"]`
          : `.menu-drawer-desktop__menu-item--mainlist[data-link-handle="${CSS.escape(handle)}"]`;
      const target = this.querySelector(selector);
      if (!(target instanceof HTMLElement)) return;
      const chevron = target.querySelector('.menu-drawer-desktop__chevron');
      if (chevron) {
        target.insertBefore(node, chevron);
      } else {
        target.appendChild(node);
      }
    });

    // Mark as processed (don't remove — keeping a stable anchor in the DOM
    // means a section morph that replaces innerHTML brings a fresh, un-marked
    // slot in, which our MutationObserver will pick up and re-apply).
    slot.setAttribute('data-applied', '');
  }
}

if (!customElements.get('header-drawer-desktop')) {
  customElements.define('header-drawer-desktop', HeaderDrawerDesktop);
}
